import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Trash2Icon, XIcon } from 'lucide-react'
import { Suspense } from 'react'

import {
  Button,
  DialogDescription,
  DialogOverlay,
  DialogTitle,
} from '@/components/ui'
import { useStatements } from '@/modules/projects'
import { LazyPdfViewer } from '@/modules/projects/components/dialogs/LazyPdfViewer'
import { useDialogActions, useDialogData, useDialogIsOpen } from '@/stores'
import { DeleteDuplicatePagesDialog } from '../dialogs/DeleteDuplicatePagesDialog'
import { DuplicateBadge } from './DuplicateBadge'
import {
  confidenceLabel,
  findDuplicateOrigin,
  pageRangeLabel,
  similarityLabel,
} from './helpers'

const dialogId = 'duplicate-compare'

// Stores ids, not segment objects, so the workspace re-reads fresh copies and closes if deleted.
export interface DuplicateCompareTarget {
  statementId: number
  duplicateSegmentId: number
}

const DocumentMissing = () => (
  <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
    No source document is attached to this statement.
  </div>
)

const Loading = () => (
  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
    Loading the document…
  </div>
)

// Side-by-side PDF comparison for a flagged repeat: the original page range on the left, the copy on
// the right. The original can live in another statement (a re-uploaded file), so each pane opens its
// own statement's PDF. Deleting here removes the copy (the original stays).
export const DuplicateCompareWorkspace = () => {
  const open = useDialogIsOpen(dialogId)
  const target = useDialogData() as DuplicateCompareTarget | undefined
  const { openDialog, closeDialog } = useDialogActions()
  const statements = useStatements()

  const statement = statements.find((item) => item.id === target?.statementId)
  const segments = statement?.segments ?? []
  const duplicate = segments.find(
    (segment) => segment.id === target?.duplicateSegmentId,
  )
  const origin = duplicate
    ? findDuplicateOrigin(statements, duplicate.duplicateOfSegmentId)
    : null
  const original = origin?.segment ?? null
  const originalStatement = origin?.statement ?? null

  // The duplicate is gone (e.g. just deleted) or never resolved: nothing to compare.
  if (!open || !statement || !duplicate) return null

  const similarity = similarityLabel(duplicate.duplicateSimilarity)
  const isCrossFile =
    originalStatement != null && originalStatement.id !== statement.id

  // Hand off to the same confirm dialog the cards use, so deleting always asks first. Opening it closes
  // this compare view (the dialog store shows one at a time), which is fine.
  const requestDelete = () =>
    openDialog(DeleteDuplicatePagesDialog.id, [
      { statement, duplicate, original, originalStatement },
    ])

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => !next && closeDialog()}
    >
      <DialogPrimitive.Portal>
        <DialogOverlay className="bg-black/40" />
        <DialogPrimitive.Content className="fixed inset-0 z-50 flex flex-col bg-background">
          <div className="flex items-start justify-between gap-3 border-b p-4">
            <div className="min-w-0 space-y-1.5">
              <DialogTitle className="truncate text-base font-semibold text-foreground">
                Compare pages — {statement.fileName}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Compare the original pages against the repeated copy before
                deleting it
              </DialogDescription>
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <DuplicateBadge />
                <span>
                  {pageRangeLabel(duplicate)} repeat {pageRangeLabel(original)}
                  {isCrossFile && ` of ${originalStatement.fileName}`}
                </span>
                <span>·</span>
                <span>{confidenceLabel(duplicate.duplicateConfidence)}</span>
                {similarity && (
                  <>
                    <span>·</span>
                    <span>{similarity}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                theme="danger"
                size="sm"
                className="cursor-pointer"
                onClick={requestDelete}
              >
                <Trash2Icon className="size-4" />
                Delete duplicate
              </Button>
              <DialogPrimitive.Close asChild>
                <Button
                  theme="ghost"
                  size="sm"
                  isIcon
                  className="cursor-pointer"
                >
                  <XIcon className="size-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </DialogPrimitive.Close>
            </div>
          </div>

          <div className="flex min-h-0 flex-1">
            {/* Left: the original page range, the one that is kept. A cross-file repeat opens the
                original's own statement PDF, not the duplicate's. */}
            <div className="flex w-1/2 flex-col border-r">
              <div className="border-b bg-emerald-50/60 px-4 py-1.5 text-xs font-medium text-emerald-700">
                Original ·{' '}
                {isCrossFile ? `${originalStatement.fileName} · ` : ''}
                {pageRangeLabel(original)} (kept)
              </div>
              <div className="min-h-0 flex-1">
                {originalStatement?.fileUrl && original ? (
                  <Suspense fallback={<Loading />}>
                    <LazyPdfViewer
                      url={originalStatement.fileUrl}
                      initialPage={original.startPage}
                      highlightRange={
                        original.startPage != null && original.endPage != null
                          ? { start: original.startPage, end: original.endPage }
                          : null
                      }
                    />
                  </Suspense>
                ) : (
                  <DocumentMissing />
                )}
              </div>
            </div>

            {/* Right: the repeated copy, the one this workspace deletes. */}
            <div className="flex w-1/2 flex-col">
              <div className="border-b bg-amber-50/70 px-4 py-1.5 text-xs font-medium text-amber-700">
                Duplicate · {pageRangeLabel(duplicate)} (will be deleted)
              </div>
              <div className="min-h-0 flex-1">
                {statement.fileUrl ? (
                  <Suspense fallback={<Loading />}>
                    <LazyPdfViewer
                      url={statement.fileUrl}
                      initialPage={duplicate.startPage}
                      highlightRange={
                        duplicate.startPage != null && duplicate.endPage != null
                          ? {
                              start: duplicate.startPage,
                              end: duplicate.endPage,
                            }
                          : null
                      }
                      highlightLabel="Duplicated"
                    />
                  </Suspense>
                ) : (
                  <DocumentMissing />
                )}
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

DuplicateCompareWorkspace.id = dialogId
